import { Paper, Stack } from '@mui/material';
import { checkIfBusinessIsBlacklistedBatch } from 'lib/api/blackList';
import { getLastActiveQuote } from 'lib/api/factoring';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ExportDialog from './components/ExportDialog';
import PayoutPlanPanel from './components/PayoutPlanPanel';
import PreviousOperationPanel from './components/PreviousOperationPanel';
import SimulationDetailsPanel from './components/SimulationDetailsPanel';

const SimulationDoneLive = (props: any) => {
  const {
    selectedRequest,
    operationDetails,
    documentsToApply,
    requestConfiguration,
    onEditing,
    setAmountOverZero,
    formData,
    setFormData,
    businessBlacklisted,
    stakeholdersBlacklisted,
    setBusinessBlacklisted,
    setStakeholdersBlacklisted,
    payoutPlan,
    setPayoutPlan,
  } = props;
  const dispatch = useDispatch();
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState<string | false>('panel1');
  const [lastActiveQuote, setLastActiveQuote] = useState<any>(null);
  const [loadingLastQuote, setLoadingLastQuote] = useState(false);

  const checkBusinessBlacklist = async () => {
    try {
      const identifiers = [selectedRequest.businessIdentifier];
      for (const document of selectedRequest.documents) {
        identifiers.push(document.stakeholderIdentifier);
      }
      const response = await checkIfBusinessIsBlacklistedBatch(
        dispatch,
        identifiers,
      );
      setBusinessBlacklisted(
        response[selectedRequest.businessIdentifier].isBlacklisted,
      );
      const stakeholdersInBlacklist = [];
      for (const document of selectedRequest.documents) {
        if (response[document.stakeholderIdentifier].isBlacklisted) {
          stakeholdersInBlacklist.push(document.stakeholderIdentifier);
        }
      }
      setStakeholdersBlacklisted(stakeholdersInBlacklist);
    } catch (error) {
      console.error('error', error);
    }
  };

  const loadLastActiveQuote = async () => {
    try {
      setLoadingLastQuote(true);
      const response = await getLastActiveQuote(
        dispatch,
        selectedRequest.businessIdentifier,
      );
      setLastActiveQuote(response);
    } catch (error) {
      console.error('Error loading last active quote:', error);
    } finally {
      setLoadingLastQuote(false);
    }
  };

  useEffect(() => {
    void checkBusinessBlacklist();
  }, [selectedRequest.businessIdentifier]);

  useEffect(() => {
    void loadLastActiveQuote();
  }, []);

  useEffect(() => {
    if (setAmountOverZero) {
      if (operationDetails.operationToDepost < 0) {
        setAmountOverZero(false);
      } else {
        setAmountOverZero(true);
      }
    }
  }, [operationDetails.operationToDepost]);

  const handleExportSimulation = async () => {
    setOpenExportDialog(true);
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          '&:hover': {
            elevation: 4,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <SimulationDetailsPanel
          expanded={expandedPanel === 'panel1'}
          onAccordionChange={handleAccordionChange}
          operationDetails={operationDetails}
          selectedRequest={selectedRequest}
          requestConfiguration={requestConfiguration}
          businessBlacklisted={businessBlacklisted}
          stakeholdersBlacklisted={stakeholdersBlacklisted}
          onExportSimulation={handleExportSimulation}
        />
      </Paper>
      <Paper
        elevation={0}
        sx={{
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          cursor: lastActiveQuote ? 'pointer' : 'default',
          opacity: lastActiveQuote ? 1 : 0.6,
          '&:hover': lastActiveQuote
            ? {
                elevation: 4,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-1px)',
              }
            : {},
        }}
      >
        <PreviousOperationPanel
          expanded={expandedPanel === 'panel2'}
          onAccordionChange={handleAccordionChange}
          lastActiveQuote={lastActiveQuote}
          loadingLastQuote={loadingLastQuote}
          setFormData={setFormData}
          formData={formData}
          onEditing={onEditing}
        />
      </Paper>
      <Paper
        elevation={0}
        sx={{
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          '&:hover': {
            elevation: 4,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <PayoutPlanPanel
          operationToDeposit={operationDetails.operationToDepost}
          expanded={expandedPanel === 'panel3'}
          businessIdentifier={selectedRequest.businessIdentifier}
          onAccordionChange={handleAccordionChange}
          payoutPlan={payoutPlan}
          setPayoutPlan={setPayoutPlan}
          onEditing={onEditing}
        />
      </Paper>

      {openExportDialog && (
        <ExportDialog
          open={openExportDialog}
          setOpen={setOpenExportDialog}
          selectedRequest={selectedRequest}
          operationDetails={operationDetails}
          documentsToApply={documentsToApply}
        />
      )}
    </Stack>
  );
};

export default SimulationDoneLive;
